// messageCenter/noticeWindow.js — one admin message (notice) in the floating window (view/floatingWindow.js): movable,
// minimisable, the page stays usable — for usage tips one reads while working (D-21). Bigplan backend/message-center,
// step 7d: opened from the notice detail ("Als Fenster öffnen"); phase 8 reuses it for notices with display mode
// 'window' on every backend page and keeps it across page changes, so it stays small (no page code in here).
//
// One window at a time: show() of another notice replaces it (the title bar carries the notice title). The text is a
// topoEditor value rendered headless — escaped text plus the entities' own markup — so innerHTML is safe.
// Stateful component: { show(notice, state?), close, isOpen, current, state, destroy }.
import '../../../stylesheet_vite/src/modules/notice-window.css';
import { str } from '../i18n/str.js';
import { renderFrontendHtmlFromValue } from '../topoEditor/topoDoc.js';
import { createFloatingWindow } from '../view/floatingWindow.js';
import { noticeTextEntities } from './noticeText.js';
import { decorateDocLinks, openDoc } from './docLinks.js';

/**
 * onClose(notice): the person closed the window (noticeWindows.js acknowledges a pending notice there).
 * onChange(notice, state): the person moved, minimised or restored it (noticeWindows.js keeps it for the next page).
 */
export function createNoticeWindow({ onClose = () => {}, onChange = () => {}, parent = document.body, env = window } = {}) {
    const entities = noticeTextEntities();
    let win = null;
    let shown = null;   // the notice in the window

    /** Opens the window with this notice (thread_id, title, body), replacing another one; state = where and how it was. */
    function show(notice, state = null) {
        if (win && shown?.thread_id === notice.thread_id) {
            win.open();   // restores a minimised window
            return;
        }
        removeWindow();
        shown = notice;
        win = createFloatingWindow({
            title: notice.title,
            iconClass: 'topo2-ic topo2-ic--mail',
            className: 'noticeWindow',
            labels: { minimize: str('MC_MINIMIZE'), restore: str('MC_RESTORE'), close: str('CLOSE_STR') },
            onClose: () => { const closed = shown; removeWindow(); onClose(closed); },
            onChange: (windowState) => onChange(shown, windowState),
            env,
        });
        const text = document.createElement('div');
        text.className = 'noticeWindow__text';
        text.innerHTML = renderFrontendHtmlFromValue(notice.body ?? '', entities);
        text.addEventListener('click', (event) => {
            const link = event.target.closest('.topo2-doclink');
            if (link) {
                event.preventDefault();
                openDoc(link);
            }
        });
        win.body.append(text);
        parent.append(win.element);
        win.open(state);
        decorateDocLinks(text);
    }

    function removeWindow() {
        if (win) { win.destroy(); }
        win = null;
        shown = null;
    }

    return {
        show,
        close: removeWindow,
        isOpen: () => Boolean(win && win.isOpen()),
        current: () => shown,
        state: () => (win ? win.getState() : null),
        destroy: removeWindow,
    };
}
